"use client"

import { useState, useEffect, useRef } from "react"
import {
  FaArrowDown,
  FaArrowUp,
  FaClipboardList,
  FaShoppingCart,
  FaHistory,
  FaFileContract,
  FaBuilding,
  FaTruck,
  FaQuestion,
  FaTimes,
  FaHome,
  FaChevronUp,
  FaChevronDown,
  FaEllipsisV,
  FaMapMarkerAlt,
  FaRegFileAlt,
  FaDatabase,
  FaCopy,
} from "react-icons/fa"
import { Link, Outlet, useLocation } from "react-router-dom"
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend as ChartLegend } from "chart.js"
import { db, ref, get, set, onValue, storage } from "../Firebase/firebase-config"
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage"
import { getDatabase, off } from "firebase/database"
import { getAuth } from "firebase/auth"
import "./DashBoard.css"
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Bar } from "recharts"
import { FaArrowTrendUp } from "react-icons/fa6";
// Import the SidebarPull component
import SidebarPull from "./sidebar-pull"
import { IoDocumentSharp } from "react-icons/io5"
import { IndianRupee } from "lucide-react"
import UserBroadcastTicker from "./UserBroadcastTicker"
import { useAccount } from "../context/AccountContext";
import { MdInfo } from "react-icons/md"


// Register Chart.js components
ChartJS.register(ArcElement, ChartTooltip, ChartLegend)

const Dashboard = () => {
  // State management
  const [companyName, setCompanyName] = useState(null)
  const [comparison, setComparison] = useState("TodayVsYesterday")
  const [workroundData, setWorkroundData] = useState({})
  const [infoDivData, setInfoDivData] = useState({})
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const cardRefs = useRef({});
  const [ordersData, setOrdersData] = useState([])
  const [stockData, setStockData] = useState({})
  const [selectedBrand, setSelectedBrand] = useState("")
  const [showAccountDetails, setShowAccountDetails] = useState(false);
  const [selectedBrandForAccount, setSelectedBrandForAccount] = useState("");
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [selectedBrandForTransactions, setSelectedBrandForTransactions] = useState("");
  const [transactions, setTransactions] = useState({});
  const [chartSize, setChartSize] = useState({
    height: 200,
    barSize: 30,
    fontSize: 13,
  })
  const [activeTooltip, setActiveTooltip] = useState(null)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [allSiteAddresses, setAllSiteAddresses] = useState([])
  const [balanceData, setBalanceData] = useState([]);


  // UI state
  const [showBrandDetails, setShowBrandDetails] = useState(null)
  const [showRefundOptions, setShowRefundOptions] = useState(false)
  const [refundIssue, setRefundIssue] = useState("")
  const [refundFiles, setRefundFiles] = useState([])
  const [selectedBrandForRefund, setSelectedBrandForRefund] = useState("")
  const [bankDetails, setBankDetails] = useState([])
  const [selectedBankDetail, setSelectedBankDetail] = useState(null)
  const [showQueries, setShowQueries] = useState(false)
  const { currentAccount } = useAccount();
  const [pendingIssues, setPendingIssues] = useState([]);
  const [pendingIssuesCount, setPendingIssuesCount] = useState(0);
  const [copyToast, setCopyToast] = useState(false);
  const [showTransportMenu, setShowTransportMenu] = useState(false);


  const auth = getAuth()
  const userUid = currentAccount?.uid;

  const location = useLocation()
  const [isSubmitting, setIsSubmitting] = useState(false)


  useEffect(() => {
    if (currentAccount) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
  }, [currentAccount]);

  const parseAdminTimestamp = (ts) => {
    if (!ts) return null;
    const fixed = ts.replace(" ", "T");
    const date = new Date(fixed);
    return isNaN(date.getTime()) ? null : date;
  };

  // ==================== LAST ORDER PLACED ====================
  const getLastOrderInfo = () => {
    if (!ordersData || Object.keys(ordersData).length === 0) {
      return { date: "No orders yet", brand: "", qty: "", site: "", count: 0 };
    }

    const ordersList = Object.values(ordersData)
      .map(order => ({
        date: parseAdminTimestamp(order.timestamp),
        brand: order.brand || "",
        qty: `${order.quantity || 0} ${order.quantityType || "bags"}`,
        site: order.siteAddress?.address || order.siteAddress || "N/A",
        fullSite: `${order.siteAddress?.address || ""}, ${order.siteAddress?.state || ""} ${order.siteAddress?.pincode ? `- ${order.siteAddress.pincode}` : ""}`.trim()
      }))
      .filter(o => o.date)
      .sort((a, b) => b.date - a.date); // newest first

    if (ordersList.length === 0) {
      return { date: "No orders yet", brand: "", qty: "", site: "", count: 0 };
    }

    const latestDate = ordersList[0].date;
    const sameDateOrders = ordersList.filter(o =>
      o.date.toDateString() === latestDate.toDateString()
    );

    return {
      date: latestDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
      brand: sameDateOrders[0].brand,
      qty: sameDateOrders[0].qty,
      site: sameDateOrders[0].fullSite,
      count: sameDateOrders.length
    };
  };

  // ==================== LAST DISPATCH ====================
  const getLastDispatchInfo = () => {
    if (!ordersData || Object.keys(ordersData).length === 0) {
      return { date: "No dispatch yet", brand: "", qty: "", site: "", count: 0 };
    }

    let dispatchList = [];

    Object.values(ordersData).forEach(order => {
      // From dispatchTransactions
      if (order.dispatchTransactions) {
        Object.values(order.dispatchTransactions).forEach(trans => {
          const date = parseAdminTimestamp(trans.timestamp);
          if (date) {
            dispatchList.push({
              date,
              brand: order.brand || "",
              qty: `${trans.dispatch || 0} ${order.quantityType || "bags"}`,
              site: order.siteAddress?.address || order.siteAddress || "N/A",
              fullSite: `${order.siteAddress?.address || ""}, ${order.siteAddress?.state || ""} ${order.siteAddress?.pincode ? `- ${order.siteAddress.pincode}` : ""}`.trim()
            });
          }
        });
      }
      // Fallback: main timestamp if status is Dispatch/Completed
      else if ((order.status === "Dispatch" || order.status === "Completed") && order.timestamp) {
        const date = parseAdminTimestamp(order.timestamp);
        if (date) {
          dispatchList.push({
            date,
            brand: order.brand || "",
            qty: `${order.quantity || 0} ${order.quantityType || "bags"}`,
            site: order.siteAddress?.address || order.siteAddress || "N/A",
            fullSite: `${order.siteAddress?.address || ""}, ${order.siteAddress?.state || ""} ${order.siteAddress?.pincode ? `- ${order.siteAddress.pincode}` : ""}`.trim()
          });
        }
      }
    });

    if (dispatchList.length === 0) {
      return { date: "No dispatch yet", brand: "", qty: "", site: "", count: 0 };
    }

    dispatchList.sort((a, b) => b.date - a.date);
    const latestDate = dispatchList[0].date;
    const sameDateDispatches = dispatchList.filter(d =>
      d.date.toDateString() === latestDate.toDateString()
    );

    return {
      date: latestDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
      brand: sameDateDispatches[0].brand,
      qty: sameDateDispatches[0].qty,
      site: sameDateDispatches[0].fullSite,
      count: sameDateDispatches.length
    };
  };

  // Add this useEffect to fetch pending issues
  useEffect(() => {
    if (!userUid) return;

    const issuesRef = ref(db, `issues/${userUid}`);

    onValue(issuesRef, (snapshot) => {
      if (snapshot.exists()) {
        const issuesData = snapshot.val();
        const pending = [];

        Object.values(issuesData).forEach(issue => {
          if (issue.status?.toLowerCase() === "pending") {
            pending.push({
              issueType: issue.issueType || "General Issue",
              description: issue.description || "",
              timestamp: issue.timestamp,
            });
          }
        });

        // Sort by latest
        pending.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        setPendingIssues(pending);
        setPendingIssuesCount(pending.length);
      } else {
        setPendingIssues([]);
        setPendingIssuesCount(0);
      }
    });
  }, [userUid]);
  /**
   * Responsive adjustments
   * Updates chart size based on screen width
   */
  useEffect(() => {
    const updateSize = () => {
      if (window.innerWidth < 768) {
        setChartSize({ height: 150, barSize: 20, fontSize: 10 })
      } else {
        setChartSize({ height: 200, barSize: 30, fontSize: 13 })
      }
    }

    updateSize()
    window.addEventListener("resize", updateSize)
    return () => window.removeEventListener("resize", updateSize)
  }, [])

  /**
   * Initialize dashboard data
   * Sets up default comparison data if none exists
   */
  useEffect(() => {
    const initializeDashboardData = async () => {
      if (!userUid) return

      const comparisonOptions = ["TodayVsYesterday", "ThisMonthVsLastMonth", "ThisWeekVsLastWeek", "ThisYearVsLastYear"]

      for (const option of comparisonOptions) {
        const dbRef = ref(db, `userComparisonData/${userUid}/comparisonOptions/${option}`)
        const snapshot = await get(dbRef)

        if (!snapshot.exists()) {
          // Default Data
          const defaultData = {
            workround: { label: "Total Workround", prev: 0, current: 0 },
            infoDivs: {
              costCalculator: { prev: 0, current: 0 },
              supplierTerritories: { prev: 0, current: 0 },
              stockAvailability: { prev: 0, current: 0 },
            },
          }

          await set(dbRef, defaultData)
        }
      }

      fetchDashboardData(userUid, "TodayVsYesterday")
      fetchUserBalances(userUid)
    }

    initializeDashboardData()
  }, [userUid])

  /**
   * Fetches user balance data
   * Retrieves and calculates total and brand-specific balances
   */
  const fetchUserBalances = (userUid) => {
    if (!userUid) return;

    const codesRef = ref(db, `new-code/${userUid}/codes`);
    const transactionsRef = ref(db, `transactions/${userUid}`);
    // Clear previous listener if needed
    off(codesRef);  // ensure only one listener is active

    onValue(codesRef, (snapshot) => {
      const codesData = snapshot.val() || {};
      const allCodeBalances = [];

      Object.entries(codesData).forEach(([codeKey, codeData]) => {
        const brand = codeData.brand || "Unknown";
        const codeID = codeData.codeID || "N/A";
        const balance = codeData.balance || 0;
        const sites = codeData.sites || {};
        const createdAtFormatted = codeData.createdAtFormatted || ""; // Fetch the timestamp

        const siteAddresses = Object.values(sites).map((site) =>
          site?.address ? `${site.address}, ${site.pincode || ""}, ${site.state || ""}` : "N/A"
        );

        allCodeBalances.push({
          brand,
          code: codeKey,
          codeID,
          balance,
          siteAddresses,
          depoAccount: codeData.depoAccount || null,
          createdAtFormatted,
        });
      });

      setBalanceData(allCodeBalances); // ✅ this should trigger UI update
    });
    // NEW: Fetch Transactions
    onValue(transactionsRef, (snapshot) => {
      const transData = snapshot.val() || {};
      const groupedByCode = {};

      Object.entries(transData).forEach(([transId, trans]) => {
        const codeKey = trans.code || trans.codeID || "unknown";

        if (!groupedByCode[codeKey]) {
          groupedByCode[codeKey] = [];
        }

        groupedByCode[codeKey].push({
          id: transId,
          ...trans,
          formattedDate: trans.timestamp
            ? new Date(trans.timestamp).toLocaleString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
            : "N/A",
        });
      });

      // Sort each group's transactions by timestamp (newest first)
      Object.keys(groupedByCode).forEach((key) => {
        groupedByCode[key].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      });

      setTransactions(groupedByCode);
    });
  };

  const parseFormattedDate = (dateString) => {
    if (!dateString) return new Date(0); // Fallback for missing timestamps

    // Example: "06/10/2025, 02:30:44 pm"
    const [datePart, timePart] = dateString.split(", ");
    const [day, month, year] = datePart.split("/").map(Number);
    let [hours, minutes, seconds] = timePart.slice(0, -3).split(":").map(Number);
    const period = timePart.slice(-2).toLowerCase();

    // Convert to 24-hour format
    if (period === "pm" && hours !== 12) hours += 12;
    if (period === "am" && hours === 12) hours = 0;

    // Create Date object (months are 0-based in JavaScript)
    return new Date(year, month - 1, day, hours, minutes, seconds);
  };
  /**
   * Fetches dashboard data based on selected comparison period
   */
  const fetchDashboardData = (userUid, selectedComparison) => {
    if (!userUid) return

    const dbRef = ref(db, `userComparisonData/${userUid}/comparisonOptions`)
    const ordersRef = ref(db, `orders/${userUid}`)

    onValue(
      dbRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val()
          let newInfoDivData = {}

          Object.keys(data).forEach((option) => {
            if (option === selectedComparison) {
              newInfoDivData = data[option]?.infoDivs || {}
            }
          })

          onValue(ordersRef, (ordersSnapshot) => {
            if (ordersSnapshot.exists()) {
              const ordersData = ordersSnapshot.val()
              const {
                prevOrders,
                currentOrders,
                prevPayments,
                currentPayments,
                prevTerritories,
                currentTerritories,
                newTerritories,
              } = calculateOrderAndPaymentsComparison(ordersData, selectedComparison)
              // Workround Calculation
              const percentageChange =
                prevOrders === 0 ? (currentOrders > 0 ? 100 : 0) : ((currentOrders - prevOrders) / prevOrders) * 100

              const newWorkroundData = {
                label: `${prevOrders} vs ${currentOrders}`,
                prev: prevOrders,
                current: currentOrders,
                percentage: percentageChange.toFixed(2),
              }

              setWorkroundData(newWorkroundData)

              // Update Cost Calculator with Payment Data
              newInfoDivData.costCalculator = {
                prev: prevPayments,
                current: currentPayments,
              }

              newInfoDivData.supplierTerritories = {
                prev: prevTerritories,
                current: currentTerritories,
                new: newTerritories,
              }

              setInfoDivData(newInfoDivData)
              setOrdersData(ordersData)
            } else {
              setOrdersData([])
            }
          })
        } else {
          console.error("No comparison data found for user:", userUid)
        }
      },
      (error) => {
        console.error("Error fetching data:", error)
      },
    )
  }

  /**
   * Calculates order and payment comparisons between time periods
   */
  const calculateOrderAndPaymentsComparison = (ordersData, comparison) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const lastYear = currentYear - 1;
    const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const lastMonthYear = currentMonth === 0 ? lastYear : currentYear;

    // We'll count using label matching like the chart
    let prevOrders = 0;
    let currentOrders = 0;
    let prevPayments = 0;
    let currentPayments = 0;
    const prevAddresses = new Set();
    const currentAddresses = new Set();

    Object.values(ordersData || {}).forEach((order) => {
      if (!order.timestamp || !order.status) return;

      const orderDate = new Date(order.timestamp);

      // Skip clearly invalid dates
      if (isNaN(orderDate.getTime())) {
        console.warn("Invalid date skipped:", order.timestamp);
        return;
      }

      let belongsToCurrent = false;
      let belongsToPrevious = false;

      if (comparison === "TodayVsYesterday") {
        const today = new Date(now);
        today.setHours(0, 0, 0, 0);
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0);

        if (orderDate.toDateString() === today.toDateString()) {
          belongsToCurrent = true;
        } else if (orderDate.toDateString() === yesterday.toDateString()) {
          belongsToPrevious = true;
        }
      }
      else if (comparison === "ThisWeekVsLastWeek") {
        const dayOfWeek = orderDate.getDay();
        const currentWeekStart = new Date(now);
        currentWeekStart.setDate(now.getDate() - now.getDay());

        const weekStartDiff = Math.floor((orderDate - currentWeekStart) / (1000 * 60 * 60 * 24 * 7));

        if (weekStartDiff === 0) {
          belongsToCurrent = true;
        } else if (weekStartDiff === -1) {
          belongsToPrevious = true;
        }
      }
      else if (comparison === "ThisMonthVsLastMonth") {
        if (
          orderDate.getFullYear() === currentYear &&
          orderDate.getMonth() === currentMonth
        ) {
          belongsToCurrent = true;
        } else if (
          orderDate.getFullYear() === lastMonthYear &&
          orderDate.getMonth() === lastMonth
        ) {
          belongsToPrevious = true;
        }
      }
      else if (comparison === "ThisYearVsLastYear") {
        if (orderDate.getFullYear() === currentYear) {
          belongsToCurrent = true;
        } else if (orderDate.getFullYear() === lastYear) {
          belongsToPrevious = true;
        }
      }

      // Count if it belongs to either period
      if (belongsToCurrent || belongsToPrevious) {
        // Count order
        if (belongsToCurrent) currentOrders++;
        if (belongsToPrevious) prevOrders++;

        // Payment (only new payments)
        if (order.paymentAmount && !order.isFromBalance) {
          const payment = Number.parseFloat(order.paymentAmount) || 0;
          if (belongsToCurrent) currentPayments += payment;
          if (belongsToPrevious) prevPayments += payment;
        }

        // Unique addresses
        if (order.siteAddress?.address) {
          const addr = order.siteAddress.address.toLowerCase();
          if (belongsToCurrent) currentAddresses.add(addr);
          if (belongsToPrevious) prevAddresses.add(addr);
        }
      }
    });

    // Calculate new territories (addresses only in current, not in previous)
    const newTerritories = [...currentAddresses].filter(
      addr => !prevAddresses.has(addr)
    ).length;

    return {
      prevOrders,
      currentOrders,
      prevPayments,
      currentPayments,
      prevTerritories: prevAddresses.size,
      currentTerritories: currentAddresses.size,
      newTerritories,
    };
  };
  /**
   * Handles comparison period change
   */
  const handleComparisonChange = (event) => {
    const newComparison = event.target.value
    setComparison(newComparison)
    fetchDashboardData(userUid, newComparison)
  }

  /**
   * Calculates percentage change between two values
   */
  const calculatePercentageChange = (current, prev) => {
    if (prev === 0) return current > 0 ? 100 : 0
    return ((current - prev) / prev) * 100
  }

  /**
   * Renders trend arrow based on percentage change
   */
  const renderArrow = (percentage) => {
    if (percentage > 0) {
      return <FaArrowUp className="trend-icon up" />
    } else if (percentage < 0) {
      return <FaArrowDown className="trend-icon down" />
    } else {
      return <span className="trend-icon neutral"></span>
    }
  }

  /**
   * Toggles tooltip visibility
   */
  const toggleTooltip = (id) => {
    setActiveTooltip(activeTooltip === id ? null : id)
  }

  /**
   * Toggles sidebar visibility
   */
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)

    // Add or remove body class for overlay
    if (!isSidebarOpen) {
      document.body.classList.add("sidebar-open")
    } else {
      document.body.classList.remove("sidebar-open")
    }
  }
  /**
   * Prepares graph data for rendering
   */
  const renderGraphData = () => {
    if (!ordersData || !comparison || Object.keys(ordersData).length === 0) {
      return [
        { name: "Mon", Pending: 0, Underloading: 0, Dispatch: 0 },
        { name: "Tue", Pending: 0, Underloading: 0, Dispatch: 0 },
        { name: "Wed", Pending: 0, Underloading: 0, Dispatch: 0 },
        { name: "Thu", Pending: 0, Underloading: 0, Dispatch: 0 },
        { name: "Fri", Pending: 0, Underloading: 0, Dispatch: 0 },
        { name: "Sat", Pending: 0, Underloading: 0, Dispatch: 0 },
        { name: "Sun", Pending: 0, Underloading: 0, Dispatch: 0 },
      ]
    }

    const now = new Date()
    const currentYear = now.getFullYear()
    const lastYear = currentYear - 1
    const currentMonth = now.getMonth()
    const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1
    const lastMonthYear = currentMonth === 0 ? lastYear : currentYear
    const statuses = ["Pending", "Underloading", "Dispatch", "Completed"]

    // Set date ranges based on comparison type
    let prevStart, prevEnd, currentStart, currentEnd
    let labels = []

    if (comparison === "TodayVsYesterday") {
      labels = ["Yesterday", "Today"]

      // Today
      currentStart = new Date(now)
      currentStart.setHours(0, 0, 0, 0)
      currentEnd = new Date(now)
      currentEnd.setHours(23, 59, 59, 999)

      // Yesterday
      prevStart = new Date(now)
      prevStart.setDate(prevStart.getDate() - 1)
      prevStart.setHours(0, 0, 0, 0)
      prevEnd = new Date(now)
      prevEnd.setDate(prevEnd.getDate() - 1)
      prevEnd.setHours(23, 59, 59, 999)
    } else if (comparison === "ThisWeekVsLastWeek") {
      labels = getWeekDays()

      // Current week
      const dayOfWeek = now.getDay()
      currentStart = new Date(now)
      currentStart.setDate(now.getDate() - dayOfWeek)
      currentStart.setHours(0, 0, 0, 0)
      currentEnd = new Date(now)
      currentEnd.setHours(23, 59, 59, 999)

      // Previous week
      prevStart = new Date(currentStart)
      prevStart.setDate(prevStart.getDate() - 7)
      prevEnd = new Date(currentStart)
      prevEnd.setDate(prevEnd.getDate() - 1)
      prevEnd.setHours(23, 59, 59, 999)
    } else if (comparison === "ThisMonthVsLastMonth") {
      labels = [getMonthName(lastMonth), getMonthName(currentMonth)]

      // Current month
      currentStart = new Date(currentYear, currentMonth, 1)
      currentEnd = new Date(now)

      // Previous month
      prevStart = new Date(lastMonthYear, lastMonth, 1)
      prevEnd = new Date(currentYear, currentMonth, 0)
      prevEnd.setHours(23, 59, 59, 999)
    } else if (comparison === "ThisYearVsLastYear") {
      labels = [`${lastYear}`, `${currentYear}`]

      // Current year
      currentStart = new Date(currentYear, 0, 1)
      currentEnd = new Date(now)

      // Previous year
      prevStart = new Date(lastYear, 0, 1)
      prevEnd = new Date(lastYear, 11, 31)
      prevEnd.setHours(23, 59, 59, 999)
    }

    const statusData = {}

    // Initialize data structure
    labels.forEach((label) => {
      statusData[label] = { name: label }
      statuses.forEach((status) => {
        statusData[label][status] = 0
      })
    })

    // Process order data
    Object.values(ordersData).forEach((order) => {
      if (!order.status || !order.timestamp) {
        return
      }

      const orderStatus = order.status.trim()
      if (!statuses.includes(orderStatus)) {
        return
      }

      const orderDate = new Date(order.timestamp)
      let label = ""

      // Determine which label this order belongs to
      if (comparison === "TodayVsYesterday") {
        if (orderDate >= currentStart && orderDate <= currentEnd) {
          label = "Today"
        } else if (orderDate >= prevStart && orderDate <= prevEnd) {
          label = "Yesterday"
        }
      } else if (comparison === "ThisWeekVsLastWeek") {
        if (orderDate >= currentStart && orderDate <= currentEnd) {
          label = orderDate.toLocaleDateString("en-US", { weekday: "short" })
        } else if (orderDate >= prevStart && orderDate <= prevEnd) {
          // For previous week, map to the equivalent day of the week
          const dayOfWeek = orderDate.getDay()
          const currentWeekDay = new Date(currentStart)
          currentWeekDay.setDate(currentWeekDay.getDate() + dayOfWeek)
          label = currentWeekDay.toLocaleDateString("en-US", { weekday: "short" })
        }
      } else if (comparison === "ThisMonthVsLastMonth") {
        if (orderDate.getFullYear() === currentYear && orderDate.getMonth() === currentMonth) {
          label = getMonthName(currentMonth)
        } else if (orderDate.getFullYear() === lastMonthYear && orderDate.getMonth() === lastMonth) {
          label = getMonthName(lastMonth)
        }
      } else if (comparison === "ThisYearVsLastYear") {
        if (orderDate.getFullYear() === currentYear) {
          label = `${currentYear}`
        } else if (orderDate.getFullYear() === lastYear) {
          label = `${lastYear}`
        }
      }

      if (label && statusData[label]) {
        // Increment the count for this status
        statusData[label][orderStatus] = (statusData[label][orderStatus] || 0) + 1

      }
    })

    return Object.values(statusData)
  }

  /**
   * Helper function to get the ISO week number
   */
  const getWeekNumber = (date) => {
    const tempDate = new Date(date.getTime())
    tempDate.setHours(0, 0, 0, 0)
    tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7))
    const week1 = new Date(tempDate.getFullYear(), 0, 4)
    return Math.round(((tempDate - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7) + 1
  }

  /**
   * Helper function to get month name
   */
  const getMonthName = (monthIndex) => {
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    return monthNames[monthIndex]
  }

  /**
   * Helper function to get the last 7 days (weekdays)
   */
  const getWeekDays = () => {
    const days = []
    for (let i = 6; i >= 0; i--) {
      const d = new Date()
      d.setDate(d.getDate() - i)
      days.push(d.toLocaleDateString("en-US", { weekday: "short" }))
    }
    return days
  }

  /**
   * Helper function to determine the label for a specific day in weekly comparison
   */
  const getDayLabel = (orderDate) => {
    return orderDate.toLocaleDateString("en-US", { weekday: "short" })
  }

  /**
   * Fetch stock data
   */
  useEffect(() => {
    const db = getDatabase()
    const stockRef = ref(db, "verifyStock")

    onValue(
      stockRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setStockData(snapshot.val())
        } else {
          setStockData({})
        }
      },
      (error) => {
        console.error("Error fetching stock data:", error)
      },
    )
  }, [])

  /**
   * Get stock status color
   */
  const getStockStatusColor = (status) => {
    switch (status) {
      case "In Stock":
        return "var(--success)"
      case "Out of Stock":
        return "var(--danger)"
      case "In Transit":
        return "var(--warning)"
      default:
        return "var(--text-dark)"
    }
  }

  /**
   * Info tooltip handlers
   */
  const dashInfo = () => {
    alert(
      "This field allows users to compare performance metrics across different time periods, helping track trends and changes over time.",
    )
  }

  const workOrderInfo = () => {
    alert(
      "This field shows the total work orders compared to the previous period. It highlights the change in order volume and percentage difference.",
    )
  }

  const costInfo = () => {
    alert(
      "This field displays cost comparisons over selected periods, helping track cost changes and manage expenses efficiently.",
    )
  }

  const supplierTerroInfo = () => {
    alert(
      "This field shows the number of supplier territories over time, highlighting changes in your supply network and new territories added.",
    )
  }

  const stockInfo = () => {
    alert(
      "Stock info....\n In Stock: Indicate that the items are in stock and ready for prompt fulfillment. \n Out of Stock: Indicates that the stock is not available and cannot be fulfilled until it is restocked. \n In Transit: Indicates that the stock is currently being transported and will be available soon. \n Note: Stock status may vary over time. Kindly contact our support team for the most accurate information.",
    )
  }

  // Add these info functions
  const lastOrderInfo = () => {
    alert("Shows the most recent order placed by you including brand, quantity, and delivery site.");
  };

  const lastDispatchInfo = () => {
    alert("Shows the most recent dispatch done including brand, dispatched quantity, and site.");
  };

  const pendingIssuesInfo = () => {
    alert("Shows your orders issues, complaints that are still pending.");
  };

  const mySitesInfo = () => {
    alert("Total number of delivery sites registered under your account.");
  };
  const balanceInfo = () => {
    const message = [
      "💰 Available Balances – Quick Guide",
      "───────────────────────────────",
      "📊 What you see here:",
      "  • Current balance for each brand & code",
      "  • Only positive balances are shown",
      "",
      "🔍 How to see more details:",
      "  • Click 'More' → view Balance and  full site addresses",
      "",
      "⚙️ Actions you can take:",
      " • 3-dot menu (⋮) → Recent Transaction",
      " • 3-dot menu (⋮) → Account Details (bank info)",
      " • 3-dot menu (⋮) → Refund Request",
      "",
      "Questions? Feel free to contact support anytime! 🙋‍♂️",
    ].join("\n");

    alert(message);
  };
  const orderStatusInfo = () => {
    alert(
      "This chart shows the status of orders:\n\n- Pending: Orders waiting for processing\n- Underloading: Orders being prepared for shipment\n- Dispatch: Orders sent out for delivery\n- Completed: Successfully delivered orders",
    )
  }

  /**
   * Check if we're on the dashboard home page
   */
  const isHomePage = () => {
    return location.pathname === "/dashboard" || location.pathname === "/"
  }

  /**
   * Format currency
   */
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount)
  }

  // NEW: Fetch bank details for selected brand
  useEffect(() => {
    const fetchBankDetails = async () => {
      if (!selectedBrandForRefund || !userUid) return

      // Find the selected brand data from balanceData
      const selectedBrandData = balanceData.find((item) => `${item.brand}-${item.codeID}` === selectedBrandForRefund)

      if (selectedBrandData && selectedBrandData.depoAccount) {
        setBankDetails([
          {
            balance: selectedBrandData.balance,
            depoAccount: selectedBrandData.depoAccount,
            brand: selectedBrandData.brand,
            codeID: selectedBrandData.codeID,
            siteAddresses: selectedBrandData.siteAddresses,
          },
        ])
      } else {
        setBankDetails([])
      }
    }

    fetchBankDetails()
  }, [selectedBrandForRefund, balanceData, userUid])

  /**
   * Handle refund submission - NEW ENHANCED VERSION
   */
  const gifUrl = "/Images/New notification.gif"

  // Helper functions
  const getIndianTimestamp = () => {
    const now = new Date()
    const istOffset = 5.5 * 60 * 60 * 1000 // IST is UTC+5:30
    const istTime = new Date(now.getTime() + istOffset)
    return istTime.toISOString().replace("T", " ").replace("Z", "")
  }

  const sendNotification = async (userId, refundId, brand, companyName) => {
    const db = getDatabase()
    const notificationRef = ref(db, `supplier_notifications/Refund/${userId}/${refundId}`)

    const newNotification = {
      brand,
      title: "Refund Request",
      message: `A new refund request has been sent by ${companyName}. Please review the refund details.`,
      gifUrl,
      timestamp: getIndianTimestamp(),
      seen: false,
    }

    try {
      await set(notificationRef, newNotification)
    } catch (error) {
      console.error("Error sending notification:", error)
    }
  }

  // NEW: Enhanced refund submission with new-code structure
  const submitRefundRequest = async () => {
    const trimmedIssue = refundIssue.trim();           // Remove leading/trailing spaces
    const cleanLength = trimmedIssue.length;           // Length after trim
    // Validation
    if (!trimmedIssue) {
      alert("Please describe your issue");
      return;
    }

    if (cleanLength < 10) {
      alert("Issue description must be at least 10 characters (excluding only leading/trailing spaces)");
      return;
    }

    if (cleanLength > 200) {
      alert("Issue description cannot exceed 200 characters");
      return;
    }

    // Both files compulsory check
    const hasLetterhead = refundFiles.some((item) => item.type === "letterhead");
    const hasCancelCheque = refundFiles.some((item) => item.type === "cancel_cheque");

    if (!hasLetterhead || !hasCancelCheque) {
      alert("Both Letterhead and Cancel Cheque are compulsory");
      return;
    }

    if (!userUid || !selectedBrandForRefund) {
      alert("Missing required information");
      return;
    }

    setIsSubmitting(true);
    const refundId = new Date().getTime();

    try {
      const fileUrls = {};

      // Upload files
      for (const { file, type } of refundFiles) {
        const folder = type === "letterhead" ? "letterhead" : "cancel_cheque";
        const filePath = `refunds/${userUid}/${folder}/${file.name}`;
        const fileRef = storageRef(storage, filePath);

        await uploadBytes(fileRef, file);
        const url = await getDownloadURL(fileRef);
        fileUrls[type] = url;
      }

      // Fetch company name
      let companyName = "Unknown Company";
      try {
        const profileRef = ref(db, `profileDashboard/${userUid}`);
        const profileSnap = await get(profileRef);
        if (profileSnap.exists()) {
          const profileData = profileSnap.val();
          if (profileData.company_name) {
            companyName = profileData.company_name;
          }
        }
      } catch (e) {
        console.warn("Failed to fetch company name:", e.message);
      }

      // Get selected brand data
      const selectedBrandData = balanceData.find(
        (item) => `${item.brand}-${item.codeID}` === selectedBrandForRefund
      );

      if (!selectedBrandData) {
        alert("Selected brand data not found");
        setIsSubmitting(false);
        return;
      }

      // Save to database (original structure for backward compatibility)
      const originalRefundsRef = ref(db, `refunds/${userUid}/${refundId}`);
      const originalRefundData = {
        brand: selectedBrandData.brand,
        codeId: selectedBrandData.codeID,
        issue: refundIssue.trim(),
        timestamp: getIndianTimestamp(),
        selectedBankDetail: selectedBrandData.depoAccount,
        balance: selectedBrandData.balance,
        files: fileUrls,
      };

      await set(originalRefundsRef, originalRefundData);

      // Send notification
      await sendNotification(userUid, refundId, selectedBrandData.brand, companyName);

      alert("Refund request submitted successfully!");

      // Reset form
      resetRefundForm();
    } catch (error) {
      console.error("Refund submission error:", error);
      alert(`Submission failed: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };
  /**
   * Handle file upload for refund requests
   */
  const handleFileUpload = (event, type) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const files = Array.from(event.target.files);

    const validFiles = files.filter((file) => {
      if (file.size > maxSize) {
        alert(`${file.name} exceeds the maximum allowed size of 5MB`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    // Prevent uploading same type multiple times
    const alreadyHasType = refundFiles.some((item) => item.type === type);
    if (alreadyHasType) {
      alert(`You have already uploaded a ${type === "letterhead" ? "Letterhead" : "Cancel Cheque"}`);
      return;
    }

    const typedFiles = validFiles.map((file) => ({ file, type }));

    setRefundFiles((prev) => [...prev, ...typedFiles]);
  };

  const resetRefundForm = () => {
    setShowRefundOptions(false);
    setRefundIssue("");
    setRefundFiles([]);
    setSelectedBrandForRefund("");
    setBankDetails([]);
    setSelectedBankDetail(null);
  };

  const handleSidebarClick = (event) => {
    // ✅ Step 1: Check if the clicked element is inside a <Link> tag
    if (event.target.closest("a")) {
      toggleSidebar() // ✅ Step 2: Close the sidebar
    }
  }

  // Improved Real-time Sites Fetcher
  useEffect(() => {
    if (!userUid) {
      setAllSiteAddresses([]);   // Clear when no user
      return;
    }

    const codesRef = ref(db, `new-code/${userUid}/codes`);

    const unsubscribe = onValue(codesRef, (snapshot) => {
      if (snapshot.exists()) {
        const codesData = snapshot.val();
        const addresses = [];
        let hasEXTransport = false;
        Object.values(codesData).forEach((codeData) => {
          const sites = codeData?.sites || {};

          Object.values(sites).forEach((site) => {
            if (site?.address) {
              addresses.push({
                address: site.address,
                pincode: site.pincode || "",
                state: site.state || "",
                fullAddress: `${site.address}, ${site.pincode || ""}, ${site.state || ""}`.trim(),
              });
            }
            // Check transportation type
            if (site?.transportation_type?.trim().toUpperCase() === "EX") {
              hasEXTransport = true;
            }
          });
        });

        setAllSiteAddresses(addresses);
        setShowTransportMenu(hasEXTransport);
      } else {
        setAllSiteAddresses([]);   // Important: clear if no sites
        setShowTransportMenu(false);
      }
    });

    // Cleanup function - prevents memory leaks and duplicate listeners
    return () => unsubscribe();

  }, [userUid]);   // Only re-run when userUid actually changes

  const getShortState = (stateName) => {
    if (!stateName) return "";
    const map = {
      "Andhra Pradesh": "AP",
      "Arunachal Pradesh": "AR",
      "Assam": "AS",
      "Bihar": "BR",
      "Chhattisgarh": "CG",
      "Goa": "GA",
      "Gujarat": "GJ",
      "Haryana": "HR",
      "Himachal Pradesh": "HP",
      "Jharkhand": "JH",
      "Karnataka": "KA",
      "Kerala": "KL",
      "Madhya Pradesh": "MP",
      "Maharashtra": "MH",
      "Manipur": "MN",
      "Meghalaya": "ML",
      "Mizoram": "MZ",
      "Nagaland": "NL",
      "Odisha": "OD",
      "Punjab": "PB",
      "Rajasthan": "RJ",
      "Sikkim": "SK",
      "Tamil Nadu": "TN",
      "Telangana": "TS",
      "Tripura": "TR",
      "Uttar Pradesh": "UP",
      "Uttarakhand": "UK",
      "West Bengal": "WB",

      // === Union Territories ===
      "Andaman and Nicobar Islands": "AN",
      "Chandigarh": "CH",
      "Dadra and Nagar Haveli and Daman and Diu": "DNHDD",
      "Delhi": "DL",
      "Jammu and Kashmir": "JK",
      "Ladakh": "LA",
      "Lakshadweep": "LD",
      "Puducherry": "PY",

      // === Special Cases ===
      "Leh Ladakh": "Leh", // for local naming
    };

    const key = Object.keys(map).find((k) =>
      stateName.toLowerCase().includes(k.toLowerCase())
    );
    return key ? map[key] : stateName;
  };

  // Extract state from the address string
  const extractFullStateName = (siteAddresses = []) => {
    if (!siteAddresses.length) return "";
    const address = siteAddresses[0]; // use first site for display
    const parts = address.split(",").map((p) => p.trim());
    return parts[parts.length - 1]; // assume last part = full state
  };

  return (
    <div className="dashboard-main-container">

      {/* Mobile Menu Toggle */}
      <button className="dashboard-mobile-menu-toggle" onClick={toggleSidebar}>
        <span></span>
        <span></span>
        <span></span>
      </button>

      {/* Sidebar Pull for mobile */}
      <SidebarPull onClick={toggleSidebar} isSidebarOpen={isSidebarOpen} />

      <div className="dashboard-container">
        {/* Left Sidebar */}
        <aside className={`dashboard-sidebar ${isSidebarOpen ? "open" : ""}`}>
          <nav className="sidebar-nav" onClick={handleSidebarClick}>
            <div className="nav-group">
              <ul>
                <li>
                  <Link
                    to="/dashboard"
                    className={`dashboard-link ${location.pathname === "/dashboard" ? "active" : ""}`}
                  >
                    <FaHome className="dash-icon" />
                    <span>Home</span>
                  </Link>
                </li>
              </ul>
            </div>

            <div className="nav-group">
              <h3>Orders</h3>
              <ul>
                <li>
                  <Link
                    to="/dashboard/product-order"
                    className={`dashboard-link ${location.pathname === "/dashboard/product-order" ? "active" : ""}`}
                  >
                    <FaShoppingCart className="dash-icon" />
                    <span>New Order</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/dashboard/stock-status"
                    className={`dashboard-link ${location.pathname === "/dashboard/stock-status" ? "active" : ""}`}
                  >
                    <FaDatabase className="dash-icon" />
                    <span>Report Stock</span>
                  </Link>
                </li>

                <li>
                  <Link
                    to="/dashboard/order-status"
                    className={`dashboard-link ${location.pathname === "/dashboard/order-status" ? "active" : ""}`}
                  >
                    <FaClipboardList className="dash-icon" />
                    <span>Order Status</span>
                  </Link>
                </li>
                {showTransportMenu && (
                  <li>
                    <Link
                      to="/dashboard/transport"
                      className={`dashboard-link ${location.pathname === "/dashboard/transport" ? "active" : ""
                        }`}
                    >
                      <FaTruck className="dash-icon" />
                      <span>Transport</span>
                    </Link>
                  </li>
                )}
                <li>
                  <Link
                    to="/dashboard/documents"
                    className={`dashboard-link ${location.pathname === "/dashboard/documents" ? "active" : ""}`}
                  >
                    <IoDocumentSharp className="dash-icon" />
                    <span>My Documents</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/dashboard/previous-orders"
                    className={`dashboard-link ${location.pathname === "/dashboard/previous-orders" ? "active" : ""}`}
                  >
                    <FaHistory className="dash-icon" />
                    <span>Order History</span>
                  </Link>
                </li>

                <li>
                  <Link
                    to="/dashboard/rate-history"
                    className={`dashboard-link ${location.pathname === "/dashboard/rate-history" ? "active" : ""}`}
                  >
                    <IndianRupee className="dash-icon" />
                    <span>Supplier Rates</span>
                  </Link>
                </li>
              </ul>
            </div>



          </nav>
        </aside>

        {/* Main Content */}
        <main className="dashboard-main">
          {isHomePage() && <UserBroadcastTicker />}

          {/* Check if we're on the home route and show dashboard content, otherwise show the Outlet */}
          {isHomePage() ? (
            <>
              {/* Available Balance Cards */}
              {balanceData.length > 0 && (
                <div className="balance-summary">
                  <div className="balance-card">
                    <div className="balance-card-header">
                      <h4>
                        Available Balances{" "}
                        <MdInfo
                          onClick={balanceInfo}
                          className="icon-i"
                        />
                      </h4>
                      {activeTooltip === "balanceSummary" && (
                        <div className="tooltip">
                          This shows your available balances for each code under different brands.
                        </div>
                      )}
                    </div>

                    <div className="brand-balances-grid">
                      {balanceData
                        .slice() // Create a shallow copy to avoid mutating the original array
                        .sort((a, b) => parseFormattedDate(b.createdAtFormatted) - parseFormattedDate(a.createdAtFormatted)) // Sort by timestamp
                        .map(({ brand, code, codeID, balance, siteAddresses, createdAtFormatted }, index) => (
                          <div className="brand-balance-card" key={`${brand}-${code}-${index}`}>
                            {/* Header with brand name and codeID */}
                            <div className="brand-balance-header">
                              <div>
                                <h4>
                                  {brand}{" "}
                                  {siteAddresses?.length > 0 && (
                                    <span className="state-short-wrapper">
                                      ({getShortState(extractFullStateName(siteAddresses))})
                                      <div className="custom-tooltip">{extractFullStateName(siteAddresses)}</div>
                                    </span>

                                  )}
                                </h4>

                                <div className="code-id">
                                  <span className="style-code"> Code ID: <strong>{codeID}</strong></span>
                                </div>
                              </div>
                              <div className="dropdown">

                                <FaEllipsisV />

                                <div className="dropdown-menu">
                                  <button
                                    className="dropdown-item"
                                    onClick={() => {
                                      setSelectedBrandForTransactions(`${brand}-${codeID}`);
                                      setShowTransactionsModal(true);
                                    }}
                                  >
                                    <FaHistory className="dropdown-icon text-primary" /> {" "}
                                    Recent Transactions
                                  </button>
                                  <button
                                    className="dropdown-item"
                                    onClick={() => {
                                      setSelectedBrandForAccount(`${brand}-${codeID}`);
                                      setShowAccountDetails(true);
                                    }}
                                  >
                                    <FaBuilding className="dropdown-icon text-primary" /> {" "}
                                    Account Details (Bank info)

                                  </button>
                                  <button
                                    className="dropdown-item"
                                    onClick={() => {
                                      setSelectedBrandForRefund(`${brand}-${codeID}`)
                                      setShowRefundOptions(true)
                                    }}
                                  >
                                    <FaRegFileAlt className="dropdown-icon text-primary" /> {" "}
                                    Refund Request
                                  </button>

                                </div>
                              </div>
                            </div>

                            {/* Main content showing balance */}

                            {showBrandDetails === `${brand}-${codeID}` && (
                              <>
                                <div className="brand-balance-content">
                                  <h5 className="text-danger fw-bold">Available Balance</h5>

                                  <div className="balance-row">
                                    <span className="balance-amount">
                                      {formatCurrency(balance)}
                                    </span>

                                    <FaArrowDown className="balance-arrow" />
                                  </div>
                                </div>
                                <div className="brand-details">


                                  <h5 className="site-title">Sites:</h5>
                                  <div className="site-address-scroll-container">
                                    {siteAddresses.length > 0 ? (
                                      siteAddresses.map((site, idx) => (
                                        <div className="site-address-card" key={idx}>
                                          <FaMapMarkerAlt className="site-icon" />
                                          <div className="site-info">
                                            <span className="site-text">{site}</span>
                                          </div>
                                        </div>
                                      ))
                                    ) : (
                                      <p className="no-sites">No site addresses available.</p>
                                    )}
                                  </div>
                                  <button className="close-details" onClick={() => setShowBrandDetails(null)}>
                                    <FaTimes />
                                  </button>
                                </div>
                              </>
                            )}

                            {/* Toggle More button */}
                            {showBrandDetails !== `${brand}-${codeID}` && (
                              <div className="more-button-wrapper">
                                <button
                                  className="more-button"
                                  onClick={() => {
                                    setShowBrandDetails(`${brand}-${codeID}`);

                                    setTimeout(() => {
                                      cardRefs.current[`${brand}-${codeID}`]
                                        ?.scrollIntoView({ behavior: "smooth", block: "start" });
                                    }, 100);
                                  }}
                                >
                                  View <b>Balance | Sites</b>  <FaChevronDown />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}
              {showTransportMenu && (
                <div className="transport-teaser-card">
                  <div className="transport-teaser-content">
                    <div className="transport-teaser-icon">
                      <FaTruck />
                    </div>

                    <div className="transport-teaser-text">
                      <h4>Transport Dashboard</h4>

                      <p>
                        Track your <b>(Ex)</b> orders, live trucks, quotes, bills & POD{" "}
                        <Link
                          to="/dashboard/transport"
                          className="transport-inline-link"
                        >
                          View Transport Dashboard <FaArrowTrendUp />
                        </Link>
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {/* Professional Overview Section - Last Order, Last Dispatch, Pending Issues + Sites */}
              <div className="activity-overview">
                <div className="activity-grid">

                  {/* Last Order Placed */}
                  <div className="activity-card last-order">
                    <div className="activity-icon">
                      <FaShoppingCart />
                      <button
                        className="info-button"
                        onClick={() => toggleTooltip("lastOrderInfo")}
                        style={{ float: "right" }}
                      >
                        <MdInfo onClick={lastOrderInfo} className="icon-i" />
                      </button>
                    </div>
                    <div className="activity-content">
                      <h4>Last Order Placed</h4>
                      <div className="activity-value">
                        {getLastOrderInfo().date}
                      </div>

                      <div className="order-info">
                        <div><strong>Brand:</strong> {getLastOrderInfo().brand || "Not available"}</div>
                        <div><strong>Qty:</strong> {getLastOrderInfo().qty || "Not available"}</div>
                        <div className="site-info-box">
                          <FaMapMarkerAlt className="mini-icon" />
                          <span>{getLastOrderInfo().site}</span>
                        </div>
                      </div>

                      {getLastOrderInfo().count > 1 && (
                        <p className="more-count">+{getLastOrderInfo().count - 1} more orders on same day</p>
                      )}
                    </div>
                    <Link to="/dashboard/previous-orders" className="activity-link">
                      View All Orders <FaArrowTrendUp />
                    </Link>
                  </div>

                  {/* Last Dispatch */}
                  <div className="activity-card last-dispatch">
                    <div className="activity-icon">
                      <FaTruck />
                      <button
                        className="info-button"
                        onClick={() => toggleTooltip("lastDispatch")}
                        style={{ float: "right" }}
                      >
                        <MdInfo onClick={lastDispatchInfo} className="icon-i" />
                      </button>
                    </div>
                    <div className="activity-content">
                      <h4>Last Dispatch</h4>
                      <div className="activity-value">
                        {getLastDispatchInfo().date}
                      </div>

                      <div className="order-info">
                        <div><strong>Brand:</strong> {getLastDispatchInfo().brand || "Not available"}</div>
                        <div><strong>Qty:</strong> {getLastDispatchInfo().qty || "Not available"}</div>
                        <div className="site-info-box">
                          <FaMapMarkerAlt className="mini-icon" />
                          <span>{getLastDispatchInfo().site}</span>
                        </div>
                      </div>

                      {getLastDispatchInfo().count > 1 && (
                        <p className="more-count">+{getLastDispatchInfo().count - 1} more dispatches on same day</p>
                      )}
                    </div>
                    <Link to="/dashboard/order-status" className="activity-link">
                      View Dispatch <FaArrowTrendUp />
                    </Link>
                  </div>

                  {/* Pending Issues - Real Data (Hide when count is 0) */}
                  {pendingIssuesCount > 0 && (
                    <div className="activity-card pending-issues">
                      <div className="activity-icon">
                        <FaQuestion />
                        <button
                          className="info-button"
                          onClick={() => toggleTooltip("pendingIssues")}
                          style={{ float: "right" }}
                        >
                          <MdInfo onClick={pendingIssuesInfo} className="icon-i" />
                        </button>
                      </div>
                      <div className="activity-content">
                        <h4>Pending Issues</h4>
                        <div className="activity-value danger">
                          {pendingIssuesCount}
                        </div>

                        <p className="activity-label">
                          {pendingIssuesCount === 1
                            ? "1 pending issue"
                            : `${pendingIssuesCount} pending issues`}
                        </p>

                        {/* Show latest pending issue */}
                        {pendingIssues.length > 0 && (
                          <div className="pending-issues-list">
                            {pendingIssues.slice(0, 1).map((issue, idx) => (
                              <div key={idx} className="issue-item">
                                <strong>{issue.issueType || "Issue"}</strong>
                                <p className="issue-desc">
                                  {issue.description
                                    ? issue.description.length > 65
                                      ? issue.description.substring(0, 65) + "..."
                                      : issue.description
                                    : "No description"}
                                </p>
                              </div>
                            ))}

                            {pendingIssues.length > 1 && (
                              <div className="more-issues">
                                +{pendingIssues.length - 1} more pending issues
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <Link to="/dashboard/raise-issue" className="activity-link">
                        See All Issues <FaArrowTrendUp />
                      </Link>
                    </div>
                  )}

                  {/* Sites Overview */}
                  <div className="activity-card sites-overview">

                    <div className="activity-icon">
                      <FaMapMarkerAlt />
                      <button
                        className="info-button"
                        onClick={() => toggleTooltip("mySites")}
                        style={{ float: "right" }}
                      >
                        <MdInfo onClick={mySitesInfo} className="icon-i" />
                      </button>
                    </div>
                    <div className="activity-content">

                      <h4>My Sites</h4>
                      <div className="activity-value">
                        {allSiteAddresses.length}
                      </div>
                      <p className="activity-label">Total delivery sites</p>

                      {allSiteAddresses.length > 0 && (
                        <div className="sites-preview">
                          {allSiteAddresses.slice(0, 2).map((site, idx) => (
                            <div key={idx} className="site-preview-item">
                              <FaMapMarkerAlt className="mini-icon" />
                              <span>{site.fullAddress}</span>
                            </div>
                          ))}
                          {allSiteAddresses.length > 2 && (
                            <div className="more-sites">+{allSiteAddresses.length - 2} more</div>
                          )}
                        </div>
                      )}
                    </div>
                    <Link to="/dashboard/stock-status" className="activity-link">
                      View All Sites <FaArrowTrendUp />
                    </Link>
                  </div>

                </div>
              </div>
              {/* Stats Cards Row */}
              {/* <div className="stats-row">
                <div className="stat-card work-orders">
                  <div className="stat-header">
                    <h3>Work Orders</h3>
                    <button className="info-button" onClick={() => toggleTooltip("workOrders")}>
                      <MdInfo onClick={workOrderInfo} className="icon-i" />
                    </button>
                    {activeTooltip === "workOrders" && (
                      <div className="tooltip">This shows the total work orders compared to the previous period.</div>
                    )}
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{workroundData?.current || 0}</div>
                    <div className="stat-comparison">
                      <div className="trend">
                        {renderArrow(calculatePercentageChange(workroundData?.current || 0, workroundData?.prev || 0))}
                        <span className="percentage">{Math.abs(workroundData?.percentage || 0).toFixed(1)}%</span>
                      </div>
                      <div className="prev-value">Previous: {workroundData?.prev || 0}</div>
                    </div>
                  </div>
                </div>

                <div className="stat-card cost-calculator">
                  <div className="stat-header">
                    <h3>Cost Calculator</h3>
                    <button className="info-button" onClick={() => toggleTooltip("costCalculator")}>
                      <MdInfo onClick={costInfo} className="icon-i" />
                    </button>
                    {activeTooltip === "costCalculator" && (
                      <div className="tooltip">This displays cost comparisons over selected periods.</div>
                    )}
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">₹{(infoDivData?.costCalculator?.current || 0).toLocaleString()}</div>
                    <div className="stat-comparison">
                      <div className="trend">
                        {renderArrow(
                          calculatePercentageChange(
                            infoDivData?.costCalculator?.current || 0,
                            infoDivData?.costCalculator?.prev || 0,
                          ),
                        )}
                        <span className="percentage">
                          {Math.abs(
                            calculatePercentageChange(
                              infoDivData?.costCalculator?.current || 0,
                              infoDivData?.costCalculator?.prev || 0,
                            ).toFixed(1),
                          )}
                          %
                        </span>
                      </div>
                      <div className="prev-value">
                        Previous: <b className="text-danger">₹</b>{" "}
                        {(infoDivData?.costCalculator?.prev || 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="stat-card territories">
                  <div className="stat-header">
                    <h3>My Sites</h3>
                    <button className="info-button" onClick={() => toggleTooltip("territories")}>
                      <MdInfo onClick={supplierTerroInfo} className="icon-i" />
                    </button>
                    {activeTooltip === "territories" && (
                      <div className="tooltip">This shows the number of your sites.</div>
                    )}
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{infoDivData?.supplierTerritories?.current || 0}</div>
                    <div className="stat-comparison">
                      <div className="territory-stats">
                        <div className="territory-stat">
                          <span className="label">Previous:</span>
                          <span className="value">{infoDivData?.supplierTerritories?.prev || 0}</span>
                        </div>
                        <div className="territory-stat">
                          <span className="label">New:</span>
                          <span className="value new">{infoDivData?.supplierTerritories?.new || 0}</span>
                        </div>
                      </div>
                    </div>
                    <div className="site-addresses-container">
                      <h5 className="site-addresses-title">Site Addresses:</h5>
                      <div className="site-addresses-scroll">
                        {allSiteAddresses.length > 0 ? (
                          allSiteAddresses.map((site, index) => (
                            <div className="site-address-bar" key={index}>
                              <FaMapMarkerAlt className="site-icon" />
                              <span className="site-address-text">{site.fullAddress}</span>
                            </div>
                          ))
                        ) : (
                          <div className="site-address-bar empty">
                            <span>No site addresses available</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div> */}
              {isHomePage() && (
                <div className="page-header">
                  <div className="comparison-selector">
                    <select onChange={handleComparisonChange} value={comparison}>
                      <option value="TodayVsYesterday">Today vs Yesterday</option>
                      <option value="ThisMonthVsLastMonth">This Month vs Last Month</option>
                      <option value="ThisWeekVsLastWeek">This Week vs Last Week</option>
                      <option value="ThisYearVsLastYear">This Year vs Last Year</option>
                    </select>
                    <MdInfo onClick={dashInfo} className="icon-i" />
                  </div>
                </div>
              )}
              <div className="dashboard-row">
                {/* Order Status Chart */}
                <div className="chart-card bar-chart">
                  <button
                    className="info-button"
                    onClick={() => toggleTooltip("orderStatusInfo")}
                    style={{ float: "right" }}
                  >
                    <MdInfo onClick={orderStatusInfo} className="icon-i" />
                  </button>
                  <h3>Order Status</h3>
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={renderGraphData()} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                        <XAxis dataKey="name" tick={{ fill: "var(--text-dark)" }} />
                        {/* <YAxis tick={{ fill: "var(--text-dark)" }} /> */}
                        <YAxis
                          tick={{ fill: "var(--text-dark)" }}
                          allowDecimals={false} // ✅ Force whole numbers
                          domain={[0, 'dataMax + 1']} // Optional: adds a little headroom
                        />

                        <Tooltip
                          contentStyle={{
                            backgroundColor: "var(--card-bg)",
                            border: "1px solid var(--border-color)",
                            borderRadius: "8px",
                          }}
                        />
                        <Legend />
                        <Bar dataKey="Pending" fill="var(--danger)" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Underloading" fill="var(--warning)" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Dispatch" fill="var(--success)" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Completed" fill="var(--info)" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Stock Availability Card */}
                <div className="card stock-availability">
                  <div className="card-header">
                    <h3>Stock Availability</h3>
                    <button className="info-button" onClick={() => toggleTooltip("stockAvailability")}>
                      <MdInfo onClick={stockInfo} className="icon-i" />
                    </button>
                    {activeTooltip === "stockAvailability" && (
                      <div className="tooltip">This shows the current stock status for different cement brands.</div>
                    )}
                  </div>
                  <div className="card-content">
                    <select
                      className="brand-select"
                      value={selectedBrand}
                      onChange={(e) => setSelectedBrand(e.target.value)}
                    >
                      <option value="">Select a brand</option>
                      {Object.keys(stockData).map((brand) => (
                        <option key={brand} value={brand}>
                          {brand}
                        </option>
                      ))}
                    </select>

                    {selectedBrand && stockData[selectedBrand] ? (
                      <div className="stock-list">
                        {Object.entries(stockData[selectedBrand]).map(([id, details]) => (
                          <div key={id} className="stock-item">
                            <div className="stock-details">
                              <div className="stock-detail">
                                <span className="stock-list-label">Depot/Plant:</span>
                                <span className="value">{details.depot || "N/A"}</span>
                              </div>
                              <div className="stock-detail">
                                <span className="stock-list-label">Area covered:</span>
                                <span className="value">{details.areaCovered || "N/A"}</span>
                              </div>
                              <div className="stock-detail">
                                <span className="stock-list-label">Grade:</span>
                                <span className="value">{details.grade || "N/A"}</span>
                              </div>
                              <div className="stock-detail">
                                <span className="stock-list-label">Status:</span>
                                <span
                                  className="value status"
                                  style={{ color: getStockStatusColor(details.stockStatus) }}
                                >
                                  {details.stockStatus || "N/A"}
                                </span>
                              </div>
                            </div>
                          </div>

                        ))}
                      </div>
                    ) : (
                      <div className="no-data">Select a brand to view stock data</div>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <Outlet />
          )}
        </main>
      </div>

      {/* Refund Request Modal */}
      {showRefundOptions && (
        <div className="refund-modal-overlay">
          <div className="refund-modal">
            <div className="refund-modal-header">
              <h3>Refund Request for {selectedBrandForRefund}</h3>
              <button
                className="close-modal"
                onClick={resetRefundForm}
              >
                <FaTimes />
              </button>
            </div>

            <div className="refund-modal-content">
              <div className="form-group">
                <label htmlFor="refundIssue">
                  Describe your issue:
                  <span style={{ fontSize: "12px", color: "#666" }}>
                    (Min 10 - Max 200 characters)
                  </span>
                </label>
                <textarea
                  id="refundIssue"
                  value={refundIssue}
                  onChange={(e) => setRefundIssue(e.target.value)}
                  placeholder="Please describe your issue in detail..."
                  rows={4}
                  maxLength={200}
                ></textarea>

                {/* Improved character counter */}
                <div style={{
                  fontSize: "12px",
                  textAlign: "right",
                  color: refundIssue.trim().length < 10 ? "#dc3545" : "#666",
                  marginTop: "4px"
                }}>
                  {refundIssue.trim().length}/200
                </div>
              </div>

              <div className="form-group">
                <label className="text-center">Upload Documents:</label>
                <div className="file-upload-container">
                  <label>
                    Letterhead:<span className="docs-star-icon">*(Max 5MB)</span>
                  </label>
                  <input
                    type="file"
                    onChange={(e) => handleFileUpload(e, "letterhead")}
                    className="file-upload"
                    accept="image/*,.pdf"
                  />

                  <label>
                    Cancel Cheque:<span className="docs-star-icon">*(Max 5MB)</span>
                  </label>
                  <input
                    type="file"
                    onChange={(e) => handleFileUpload(e, "cancel_cheque")}
                    className="file-upload"
                    accept="image/*,.pdf"
                  />
                </div>

                {/* Show uploaded files */}
                {refundFiles.length > 0 && (
                  <div className="refund-uploaded-files">
                    <p className="uploaded-files-title">Uploaded Documents:</p>
                    <div className="uploaded-files-list">
                      {refundFiles.map((item, index) => (
                        <div key={index} className="uploaded-file-item">
                          <span className="check-icon">✓</span>
                          <span className="file-name">
                            {item.type === "letterhead" ? "Letterhead" : "Cancel Cheque"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="refund-modal-footer">
              <button
                className="cancels-button"
                onClick={resetRefundForm}
              >
                Cancel
              </button>
              <button
                className="refund-submits-button"
                onClick={submitRefundRequest}
                disabled={
                  isSubmitting ||
                  !refundIssue.trim() ||
                  refundIssue.trim().length < 10 ||
                  refundFiles.length < 2 ||   // Both files required
                  !refundFiles.some(f => f.type === "letterhead") ||
                  !refundFiles.some(f => f.type === "cancel_cheque")
                }
              >
                {isSubmitting ? (
                  <>
                    <span className="refund-spinner" /> Submitting...
                  </>
                ) : (
                  "Submit Refund Request"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAccountDetails && (
        <div className="refund-modal-overlay">
          <div className="refund-modal account-details-modal">
            <div className="refund-modal-header">
              <h3>Account Details</h3>
              <button
                className="close-modal"
                onClick={() => {
                  setShowAccountDetails(false);
                  setSelectedBrandForAccount("");
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div className="refund-modal-content">
              {(() => {
                const selected = balanceData.find(
                  (item) => `${item.brand}-${item.codeID}` === selectedBrandForAccount
                );

                if (!selected || !selected.depoAccount) {
                  return <p className="no-data">No account details available for this brand/code.</p>;
                }

                const acc = selected.depoAccount;

                return (
                  <div className="account-details-content">
                    {/* Header */}
                    <div className="account-header">
                      <div>
                        <h4 className="brand-name">{selected.brand}</h4>
                        <div className="code-label">
                          Code ID: <strong>{selected.codeID}</strong>
                        </div>
                      </div>
                    </div>

                    {/* Bank info + copy icon wrapper */}
                    <div className="bank-info-container">
                      <div className="info-row">
                        <div className="label">Available Balance</div>
                        <div className="value-group">
                          <span className="value highlight">{formatCurrency(selected.balance)}</span>
                        </div>
                      </div>

                      <div className="info-row">
                        <div className="label">Beneficiary Name</div>
                        <div className="value-group">
                          <span className="value">{acc.BeneficiaryName || "—"}</span>
                        </div>
                      </div>

                      <div className="info-row">
                        <div className="label">Account Number</div>
                        <div className="value-group">
                          <span className="value font-mono">{acc.AccountNumber || "—"}</span>
                        </div>
                      </div>

                      <div className="info-row">
                        <div className="label">Branch</div>
                        <div className="value-group">
                          <span className="value">{acc.Branch || "—"}</span>
                        </div>
                      </div>

                      <div className="info-row">
                        <div className="label">IFSC Code</div>
                        <div className="value-group">
                          <span className="value font-mono">{acc.IFSC || "—"}</span>
                        </div>
                      </div>

                      {/* Floating copy icon – bottom right */}
                      <button
                        className="copy-icon-btn"
                        onClick={() => {
                          const text = `Bank Account Details

Beneficiary Name : ${acc.BeneficiaryName || "—"}
Account Number   : ${acc.AccountNumber || "—"}
Branch           : ${acc.Branch || "—"}
IFSC Code        : ${acc.IFSC || "—"}
`;

                          navigator.clipboard.writeText(text).then(() => {
                            setCopyToast(true);

                            setTimeout(() => {
                              setCopyToast(false);
                            }, 2000);
                          });
                        }}
                        title="Copy bank details (excluding balance)"
                      >
                        <FaCopy />
                      </button>
                    </div>
                  </div>
                );
              })()}

              {copyToast && (
                <div className="copy-toast">
                  <FaCopy />
                  <span>Bank details copied successfully!</span>
                </div>
              )}
            </div>

            <div className="refund-modal-footer">
              <button
                className="cancel-button"
                onClick={() => {
                  setShowAccountDetails(false);
                  setSelectedBrandForAccount("");
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showTransactionsModal && (
        <div className="modal-overlay">
          <div className="trans-modal-unique">

            <div className="trans-modal-header-unique">
              <h4> Recent Transactions - {selectedBrandForTransactions}</h4>
              <button
                className="trans-close-btn-unique"
                onClick={() => {
                  setShowTransactionsModal(false);
                  setSelectedBrandForTransactions("");
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div className="trans-modal-content-unique">
              {(() => {
                const selected = balanceData.find(
                  (item) => `${item.brand}-${item.codeID}` === selectedBrandForTransactions
                );

                const transList = selected
                  ? (transactions[selected.codeID] || transactions[selected.code] || [])
                  : [];

                return transList.length > 0 ? (
                  <table className="trans-table-unique">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Rate</th>
                        <th>Dispatch Qty</th>
                        <th>Credit (₹)</th>
                        <th>Debit (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transList.map((trans, index) => {
                        const isCredit = trans.type === 'credit';
                        return (
                          <tr key={index} className={isCredit ? 'trans-credit-row' : 'trans-debit-row'}>
                            <td className="trans-date-col">
                              {trans.formattedDate ? trans.formattedDate : new Date(trans.timestamp).toLocaleDateString('en-IN')}
                            </td>

                            <td className="trans-rate-col">
                              {trans.rate ? `₹${Number(trans.rate).toFixed(2)}` : '—'}
                            </td>
                            <td className="trans-qty-col">
                              {trans.dispatchQty || trans.dispatch
                                ? `${trans.dispatchQty || trans.dispatch} ${trans.quantityType || 'bags'}`
                                : '—'}
                            </td>
                            <td className="trans-credit-col">
                              {isCredit ? (
                                <span className="positive">+ ₹{Number(trans.amount || 0).toLocaleString('en-IN')}</span>
                              ) : '—'}
                            </td>
                            <td className="trans-debit-col">
                              {!isCredit ? (
                                <span className="negative">- ₹{Number(trans.amount || 0).toLocaleString('en-IN')}</span>
                              ) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-transactions-modal">
                    <p className="text-center text-danger">No transactions found for this code yet.</p>
                  </div>
                );
              })()}
            </div>

            <div className="trans-modal-footer-unique">
              <button
                className="cancel-button"
                onClick={() => {
                  setShowTransactionsModal(false);
                  setSelectedBrandForTransactions("");
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Mobile overlay */}
      {isSidebarOpen && <div className="mobile-overlay" onClick={toggleSidebar}></div>}
    </div>
  )
}

export default Dashboard
